import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import { AuthContext } from '../../context/AuthContext';
import { getTop50Url, getMyRankUrl, getPrizePoolCurrentUrl } from '../../api/generated/game-punch/game-punch.ts';
import ArrowLeftImage from '../../assets/icons/arrow_left.png';
import RankingAvatarImage from '../../assets/images/punch/ranking_avata.png';

const PUBLIC = process.env.PUBLIC_URL;
const IMG_CROWN = `${PUBLIC}/assets/punch/rk-crown.png`;
const IMG_BADGE2 = `${PUBLIC}/assets/punch/rk-medal-2nd.png`;
const IMG_BADGE3 = `${PUBLIC}/assets/punch/rk-medal-3rd.png`;
const IMG_NUM1 = `${PUBLIC}/assets/punch/rk-num1.svg`;
const IMG_NUM2 = `${PUBLIC}/assets/punch/rk-num2.svg`;
const IMG_NUM2S = `${PUBLIC}/assets/punch/rk-num2-stroke.svg`;
const IMG_NUM3 = `${PUBLIC}/assets/punch/rk-num3.svg`;
const IMG_WAVE = `${PUBLIC}/assets/punch/rk-wave.svg`;

const FALLBACK_INVITE_CODE = '-';

function normalizeProfileImageUrl(url) {
  if (!url) return '';
  return url.startsWith('http://') ? url.replace(/^http:\/\//, 'https://') : url;
}

function formatDateLabel(dateValue) {
  if (!dateValue) return '';
  const raw = String(dateValue);
  const matched = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (matched) return `${matched[1]}.${matched[2]}.${matched[3]}`;

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}.${String(parsed.getMonth() + 1).padStart(2, '0')}.${String(parsed.getDate()).padStart(2, '0')}`;
  }
  return raw;
}

function formatSeasonDateRange(startDate, endDate) {
  const start = formatDateLabel(startDate);
  const end = formatDateLabel(endDate);
  if (!start || !end) return '-';
  return `${start} ~ ${end}`;
}

function ChevronLeft() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ChevronRight() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PodiumAvatar({ src, alt = '' }) {
  return (
    <img
      src={src || RankingAvatarImage}
      alt={alt}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  );
}

function ScoreBadge({ nyangScore, rewardPoint }) {
  return (
    <div
      style={{
        background: 'rgba(0,0,0,0.12)',
        borderRadius: 5,
        padding: '5px 15px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        marginTop: 5,
      }}
    >
      <span
        style={{
          fontFamily: 'Pretendard',
          fontSize: 14,
          fontWeight: 700,
          color: '#ffbc2b',
          whiteSpace: 'nowrap',
          lineHeight: 1.3,
        }}
      >
        {(rewardPoint ?? 0).toLocaleString()} P
      </span>
      <span
        style={{
          fontFamily: 'Pretendard',
          fontWeight: 'bold',
          fontSize: 10,
          color: '#f4f4f4',
          whiteSpace: 'nowrap',
          lineHeight: 1.3,
        }}
      >
        {(nyangScore ?? 0).toLocaleString()}스코어
      </span>
    </div>
  );
}

function Podium1st({ info }) {
  return (
    <div style={{ width: 90, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ position: 'relative', width: 90, height: 124, flexShrink: 0 }}>
        <div
          style={{
            position: 'absolute',
            left: -8,
            top: 26,
            width: 104,
            height: 103,
            overflow: 'hidden',
            borderRadius: '50%',
            maskImage: 'radial-gradient(circle 45px at 52px 51.5px, black 45px, transparent 46px)',
            WebkitMaskImage: 'radial-gradient(circle 45px at 52px 51.5px, black 45px, transparent 46px)',
          }}
        >
          <PodiumAvatar src={info?.profileImageUrl} />
        </div>
        <img
          src={IMG_CROWN}
          alt="1위 왕관"
          style={{
            position: 'absolute',
            width: 40,
            height: 40,
            left: 'calc(50% - 1px)',
            transform: 'translateX(-50%)',
            top: 1,
            zIndex: 2,
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'absolute', left: 40, top: 20, width: 7, height: 12, zIndex: 3 }}>
          <div style={{ position: 'absolute', inset: '-24.09% -42.61% -41.78% -42.61%' }}>
            <img src={IMG_NUM1} alt="" style={{ display: 'block', width: '100%', height: '100%' }} />
          </div>
        </div>
      </div>

      {info ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 5 }}>
            <span
              style={{
                fontFamily: 'Pretendard',
                fontSize: 14,
                fontWeight: 700,
                color: 'white',
                lineHeight: 1.3,
                maxWidth: 90,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {info.nickname}
            </span>
            <span style={{ fontFamily: 'Pretendard', fontSize: 12, color: '#d9d9d9', lineHeight: 1.3 }}>
              {info.inviteCode || FALLBACK_INVITE_CODE}
            </span>
          </div>
          <ScoreBadge nyangScore={info.nyangScore} rewardPoint={info.rewardPoint} />
        </>
      ) : null}
    </div>
  );
}

function Podium2nd({ info }) {
  return (
    <div style={{ width: 90, paddingTop: 40, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ position: 'relative', width: 100, height: 127, flexShrink: 0 }}>
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 27,
            width: 100,
            height: 100,
            borderRadius: '50%',
            overflow: 'hidden',
          }}
        >
          <PodiumAvatar src={info?.profileImageUrl} />
        </div>
        <div style={{ position: 'absolute', left: 30, top: 0, width: 40, height: 40, zIndex: 2 }}>
          <img
            src={IMG_BADGE2}
            alt="2위"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
          />
        </div>
        <div style={{ position: 'absolute', left: 30 + 16.6, top: 18.83, width: 6.023, height: 8.302, zIndex: 3 }}>
          <div style={{ position: 'absolute', inset: '-24.09% -66.41% -72.27% -66.41%' }}>
            <img src={IMG_NUM2} alt="" style={{ display: 'block', width: '100%', height: '100%' }} />
          </div>
        </div>
        <div style={{ position: 'absolute', left: 30 + 15.77, top: 18, width: 7.69, height: 9.968, zIndex: 4 }}>
          <img
            src={IMG_NUM2S}
            alt=""
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
      </div>

      {info ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 5 }}>
            <span
              style={{
                fontFamily: 'Pretendard',
                fontSize: 14,
                fontWeight: 700,
                color: 'white',
                lineHeight: 1.3,
                maxWidth: 90,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {info.nickname}
            </span>
            <span style={{ fontFamily: 'Pretendard', fontSize: 12, color: '#d9d9d9', lineHeight: 1.3 }}>
              {info.inviteCode || FALLBACK_INVITE_CODE}
            </span>
          </div>
          <ScoreBadge nyangScore={info.nyangScore} rewardPoint={info.rewardPoint} />
        </>
      ) : null}
    </div>
  );
}

function Podium3rd({ info }) {
  return (
    <div style={{ width: 90, paddingTop: 40, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ position: 'relative', width: 90, height: 119, flexShrink: 0 }}>
        <div
          style={{
            position: 'absolute',
            left: -3,
            top: 25,
            width: 98,
            height: 98,
            borderRadius: '50%',
            overflow: 'hidden',
          }}
        >
          <PodiumAvatar src={info?.profileImageUrl} />
        </div>
        <div style={{ position: 'absolute', left: 26, top: 0, width: 40, height: 40, zIndex: 2 }}>
          <img
            src={IMG_BADGE3}
            alt="3위"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
          />
        </div>
        <div style={{ position: 'absolute', left: 42, top: 18, width: 7.606, height: 9.968, zIndex: 3 }}>
          <div style={{ position: 'absolute', inset: '-11.88% -41.87% -52.02% -41.88%' }}>
            <img src={IMG_NUM3} alt="" style={{ display: 'block', width: '100%', height: '100%' }} />
          </div>
        </div>
      </div>

      {info ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 5 }}>
            <span
              style={{
                fontFamily: 'Pretendard',
                fontSize: 14,
                fontWeight: 700,
                color: 'white',
                lineHeight: 1.3,
                maxWidth: 90,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {info.nickname}
            </span>
            <span style={{ fontFamily: 'Pretendard', fontSize: 12, color: '#d9d9d9', lineHeight: 1.3 }}>
              {info.inviteCode || FALLBACK_INVITE_CODE}
            </span>
          </div>
          <ScoreBadge nyangScore={info.nyangScore} rewardPoint={info.rewardPoint} />
        </>
      ) : null}
    </div>
  );
}

function RankRow({ info }) {
  const { isMe } = info;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderRadius: 10,
        width: '100%',
        background: isMe ? '#8b68dd' : 'white',
        boxShadow: '0px 5px 16px 0px rgba(0,0,0,0.1)',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span
            style={{
              fontFamily: 'Pretendard',
              fontSize: 14,
              fontWeight: 700,
              color: isMe ? 'white' : '#5e5e5e',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
            }}
          >
            {info.ranking}
          </span>
        </div>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            overflow: 'hidden',
            background: '#d9d9d9',
            flexShrink: 0,
          }}
        >
          {info.profileImageUrl ? (
            <img
              src={info.profileImageUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : null}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span
            style={{
              fontFamily: 'Pretendard',
              fontSize: 14,
              fontWeight: 700,
              color: isMe ? 'white' : '#5e5e5e',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
            }}
          >
            {info.nickname}
          </span>
          <span
            style={{
              fontFamily: 'Pretendard',
              fontSize: 12,
              color: isMe ? '#f4f4f4' : '#919191',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
            }}
          >
            {info.inviteCode || FALLBACK_INVITE_CODE}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <span
          style={{
            fontFamily: 'Pretendard',
            fontSize: 14,
            fontWeight: 700,
            color: isMe ? 'white' : '#333',
            lineHeight: 1.3,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {(info.rewardPoint ?? 0).toLocaleString()}P
        </span>
        <div
          style={{
            width: 64,
            background: '#e2e6ff',
            borderRadius: 20,
            padding: '3px 5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontFamily: 'Pretendard',
              fontSize: 10,
              color: '#5e5e5e',
              lineHeight: 1.3,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {(info.nyangScore ?? 0).toLocaleString()}스코어
          </span>
        </div>
      </div>
    </div>
  );
}

export default function PunchRankingPage() {
  const navigate = useNavigate();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const [selectedSeasonNo, setSelectedSeasonNo] = useState(null);

  const fetcher = useCallback(async (...args) => fetchWithToken(...args), [fetchWithToken]);

  const { data: prizePoolCurrentData } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getPrizePoolCurrentUrl()}`, accessToken] : null,
    fetcher,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  const { data: top50Data } = useSWR(
    accessToken && selectedSeasonNo
      ? [`${API_SERVER_DOMAIN}${getTop50Url({ seasonNo: selectedSeasonNo })}`, accessToken]
      : null,
    fetcher,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  const { data: myRankData } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getMyRankUrl()}`, accessToken] : null,
    fetcher,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  useEffect(() => {
    if (!prizePoolCurrentData?.seasonNo) return;
    setSelectedSeasonNo((prev) => prev ?? prizePoolCurrentData.seasonNo);
  }, [prizePoolCurrentData?.seasonNo]);

  const rankings = top50Data?.infos ?? [];
  const myRanking = myRankData?.myRanking;
  const currentSeasonNo = prizePoolCurrentData?.seasonNo;
  const seasonDateRange = formatSeasonDateRange(top50Data?.seasonStartDate, top50Data?.seasonEndDate);
  const canGoPrevSeason = (selectedSeasonNo ?? 1) > 1;
  const canGoNextSeason = currentSeasonNo ? (selectedSeasonNo ?? currentSeasonNo) < currentSeasonNo : true;

  const top3 = rankings.slice(0, 3).map((r) => ({
    ...r,
    inviteCode: r.inviteCode || FALLBACK_INVITE_CODE,
    profileImageUrl: normalizeProfileImageUrl(r.profileImageUrl),
    isMe: String(r.ranking) === String(myRanking),
  }));
  const rest = rankings.slice(3).map((r) => ({
    ...r,
    inviteCode: r.inviteCode || FALLBACK_INVITE_CODE,
    profileImageUrl: normalizeProfileImageUrl(r.profileImageUrl),
    isMe: String(r.ranking) === String(myRanking),
  }));

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: '#e2e6ff' }}>
      <div
        style={{
          position: 'absolute',
          left: 0,
          width: '100%',
          height: 398,
          background: 'linear-gradient(180deg, #3b5cd2 0%, #8b68dd 100%)',
        }}
      />

      <header
        style={{
          position: 'relative',
          zIndex: 10,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
        }}
        aria-label="상단 네비게이션"
      >
        <button
          type="button"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            border: 0,
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
          }}
          onClick={() => navigate(-1)}
          aria-label="뒤로가기"
        >
          <img
            src={ArrowLeftImage}
            alt=""
            style={{ width: 24, height: 24, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
          />
        </button>
      </header>

      <div style={{ position: 'relative', zIndex: 10 }}>
        <div
          style={{
            height: 46,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '7px 69px 9px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              style={{
                background: 'transparent',
                border: 0,
                padding: 0,
                cursor: canGoPrevSeason ? 'pointer' : 'default',
                display: 'flex',
                opacity: canGoPrevSeason ? 1 : 0.35,
              }}
              aria-label="이전 시즌"
              onClick={() => {
                if (!canGoPrevSeason) return;
                setSelectedSeasonNo((prev) => (prev && prev > 1 ? prev - 1 : prev));
              }}
              disabled={!canGoPrevSeason}
            >
              <ChevronLeft />
            </button>
            <span
              style={{
                fontFamily: 'Pretendard',
                fontSize: 14,
                fontWeight: 700,
                color: 'white',
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
              }}
            >
              {seasonDateRange}
            </span>
            <button
              type="button"
              style={{
                background: 'transparent',
                border: 0,
                padding: 0,
                cursor: canGoNextSeason ? 'pointer' : 'default',
                display: 'flex',
                opacity: canGoNextSeason ? 1 : 0.35,
              }}
              aria-label="다음 시즌"
              onClick={() => {
                if (!canGoNextSeason) return;
                setSelectedSeasonNo((prev) => (prev ? prev + 1 : prev));
              }}
              disabled={!canGoNextSeason}
            >
              <ChevronRight />
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '90px 90px 90px',
            columnGap: 25,
            justifyContent: 'center',
            alignItems: 'start',
            paddingTop: 10,
            minHeight: 263,
          }}
        >
          <div>
            <Podium2nd info={top3[1]} />
          </div>
          <div>
            <Podium1st info={top3[0]} />
          </div>
          <div>
            <Podium3rd info={top3[2]} />
          </div>
        </div>

        <div style={{ position: 'relative', width: '100%', height: 60, overflow: 'hidden', flexShrink: 0 }}>
          <div style={{ position: 'absolute', top: '33.33%', left: 0, right: 0, bottom: 0 }}>
            <img
              src={IMG_WAVE}
              alt=""
              style={{ position: 'absolute', inset: 0, display: 'block', width: '100%', height: '100%' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 10px 40px' }}>
          {rest.length === 0 && rankings.length === 0 && (
            <div style={{ display: 'grid', justifyContent: 'center', padding: '40px 0' }}>
              <p style={{ fontFamily: 'Pretendard', fontSize: 14, color: '#5e5e5e', fontWeight: 700 }}>이번 주 랭킹이 아직 없어요.</p>
              <p style={{ fontFamily: 'Pretendard', fontSize: 12, color: '#5e5e5e' }}>참가자가 모이면 순위가 표시됩니다.</p>
            </div>
          )}
          {rest.map((info) => (
            <RankRow key={info.userNo ?? info.ranking} info={info} />
          ))}
        </div>
      </div>
    </div>
  );
}
